import { useQuery, gql } from '@apollo/client'
import AppsIcon from '@mui/icons-material/Apps'
import FolderIcon from '@mui/icons-material/Folder'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import ImageSearchIcon from '@mui/icons-material/ImageSearch'
import SearchIcon from '@mui/icons-material/Search'
import TocIcon from '@mui/icons-material/Toc'
import WorkIcon from '@mui/icons-material/Work'
import { Divider } from '@mui/material'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Grow from '@mui/material/Grow'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import {
  Query,
  DigitalAsset,
  RetailerAccount,
} from '@promoboxx/graphql-gateway-types'
import useSimpleFilter from '@promoboxx/use-filter/dist/useSimpleFilter'
import I18n from 'i18n-js'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'

import { Breadcrumb, Breadcrumbs } from '@src/components/shared/Breadcrumbs'
import urlParamStore from '@src/lib/urlParamStore'
import usePreviouslySelectedBrand from '@src/lib/usePreviouslySelectedBrand'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'

import FilterContentLayoutWrapper from '../shared/FilterContentLayoutWrapper'

import DigitalAssetCard from './DigitalAssetCard'
import DigitalAssetDialog from './DigitalAssetDialog'
import DigitalAssetTable, { DigitalAssetRow } from './DigitalAssetTable'
import downloadablesPaths from './downloadablesPaths'
import Filters, { DownloadblesFilters } from './Filters'
import SelectAll from './SelectAll'
import SelectedAssetsFooter from './SelectedAssetsFooter'
import { useCurrentIds } from './useCurrentIds'

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: theme.spacing(6),
    display: 'flex',
    flexDirection: 'column',
  },
  loading: {
    width: '100%',
    height: 'calc(100vh - 120px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  assetsContainer: {
    display: 'flex',
    flexGrow: 1,
  },
  assetsInnerContainer: {
    // padding: theme.spacing(4),
    width: '100%',
  },
  folderContent: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px',
  },
  folderText: {
    marginLeft: theme.spacing(4),
    textDecoration: 'none',
  },
}))

export type SelectedAssets = Record<string, DigitalAsset>

const Downloadables = () => {
  const history = useHistory()
  const { classes } = useStyles()
  const { pathname } = useLocation()
  const { resource_id, resource_type, parent_id, brands } = useCurrentIds()
  const [selected, setSelected] = useState<SelectedAssets>({})
  const [displayType, setDisplayType] = useState<'grid' | 'table'>('grid')
  const [assetPreview, setAssetPreview] = useState<DigitalAsset | null>(null)

  const { previouslySelectedBrands, setPreviouslySelectedBrand } =
    usePreviouslySelectedBrand()

  const filterApi = useSimpleFilter<DownloadblesFilters>('downloadables', {
    defaultFilterInfo: {
      filter: {
        tags: [] as string[],
        search: '',
        brand: '',
        brands: previouslySelectedBrands.map((x) => String(x.id)),
      },
    },
    store: urlParamStore,
    debounceDuration: 200,
  })

  useEffect(() => {
    setPreviouslySelectedBrand(filterApi.filterInfo.filter.brands)
  }, [filterApi.filterInfo.filter.brands, setPreviouslySelectedBrand])

  const params = downloadablesPaths.index.useParams()

  const currentRetailerAccount: RetailerAccount = useSelector(
    currentRetailerAccountSelector,
  )

  const brandIdFromFilter = useMemo(() => {
    let brandIdFromFilter: string | undefined
    if (filterApi.filterInfo.filter.brands[0]) {
      brandIdFromFilter = filterApi.filterInfo.filter.brands[0]
    }

    return brandIdFromFilter
  }, [filterApi.filterInfo.filter.brands])

  const selectedTags = filterApi.filterInfo.filter.tags
  const searchTerm = filterApi.filterInfo.filter.search
  const debouncedSearchTerm = filterApi.debouncedFilterInfo.filter.search
  const setSearchTerm = (search: string) =>
    filterApi.updateFilter({
      search,
    })
  const setSelectedTags = (tags: string[]) =>
    filterApi.updateFilter({ tags }, true)

  const { data: folderData } = useQuery<Query>(GET_FOLDERS, {
    variables: {
      params: {
        resource_type,
        resource_id,
        filterParams: { folders_only: true },
      },
    },
  })

  // Clear selected assets when filters / retailer account changes.
  useEffect(() => {
    setSelected({})
  }, [filterApi.filterInfo.filter, params.retailerAccountSlug])

  // Redirect to `/root` if it's not present.
  useEffect(() => {
    if (!parent_id) {
      history.replace(`${pathname}/root`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent_id])

  const conditionalParams =
    selectedTags.length || searchTerm.length
      ? {
          filterParams: {
            tags: selectedTags.join(','),
          },
        }
      : { by_ancestry: true }

  const { loading: gqlLoading, data } = useQuery<Query>(GET_DIGITAL_ASSETS, {
    variables: {
      params: {
        resource_type: 'retailer_account',
        resource_id: currentRetailerAccount.id,
        search: debouncedSearchTerm,
        fields: [
          'is_folder',
          'tag_list',
          'associable_id',
          'digital_asset_associable_id',
          'file_url',
        ],
        filter_brand_id: brandIdFromFilter,
        ...(parent_id === 'root' || selectedTags.length ? {} : { parent_id }),
        ...conditionalParams,
      },
    },
  })

  const loading = filterApi.isLoading || gqlLoading

  const allFolders = folderData?.getDigitalAssets || []
  const filesAndFolders = data?.getDamAssets?.assets || []
  const currentPath = data?.getDamAssets?.path || ''

  const assetsSelected = Object.keys(selected).length

  const breadcrumbs = createBreadCrumbs({
    currentPath,
    allFolders,
  })

  const currentFolders = filesAndFolders.filter(
    (digitalAsset: DigitalAsset) => digitalAsset?.is_folder === true,
  )

  const digitalAssets = filesAndFolders.filter(
    (digitalAsset: DigitalAsset) => digitalAsset?.is_folder === false,
  )

  const handleSelect = (
    event: React.MouseEvent,
    isSelected: boolean,
    digitalAsset: DigitalAsset,
  ) => {
    const { id } = digitalAsset
    if (isSelected) {
      setSelected({ ...selected, [id]: digitalAsset })
    } else if (selected[id]) {
      // Use destructuring to remove asset by id
      const { [id]: value, ...rest } = selected
      setSelected(rest)
    }
  }

  const handleShowPreview = (digitalAsset: DigitalAsset) => {
    setAssetPreview(digitalAsset)
  }

  const handlePreviewClose = () => {
    setAssetPreview(null)
  }

  const handleDisplayTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: 'table' | 'grid',
  ) => {
    setDisplayType(newAlignment)
  }

  const DownloadablesWrapper = FilterContentLayoutWrapper

  return (
    <DownloadablesWrapper
      title={I18n.t('downloadables.downloadables')}
      slot={{
        filter: <Filters filterApi={filterApi} />,
        breadCrumbs: (
          <Breadcrumbs>
            <Breadcrumb
              Icon={WorkIcon}
              to="root"
              onClick={() => {
                setSelectedTags([])
                setSearchTerm('')
              }}
            >
              {I18n.t('downloadables.allDownloadables')}
            </Breadcrumb>

            {selectedTags.length > 0 || searchTerm.length > 0 ? (
              <Breadcrumb Icon={SearchIcon}>
                {I18n.t('downloadables.filterResults')}
              </Breadcrumb>
            ) : (
              breadcrumbs.map((breadcrumb) => (
                <Breadcrumb
                  Icon={FolderOutlinedIcon}
                  to={breadcrumb.to}
                  key={breadcrumb.name}
                >
                  {breadcrumb.name}
                </Breadcrumb>
              ))
            )}
          </Breadcrumbs>
        ),
      }}
      slotProps={{
        breadCrumb: {
          to: 'root',
        },
      }}
    >
      <div>
        <div className={classes.assetsContainer}>
          <div className={classes.assetsInnerContainer}>
            {loading ? (
              <div className={classes.loading}>
                <CircularProgress />
              </div>
            ) : (
              <>
                {currentFolders.length > 0 && selectedTags.length === 0 && (
                  <>
                    <Typography variant="h6">
                      {I18n.t('downloadables.folders')}
                    </Typography>
                    <Divider sx={{ marginTop: 1, marginBottom: 2 }} />
                    <Grid container spacing={4}>
                      {currentFolders.map((folder: DigitalAsset | null) => (
                        <Grid key={folder?.id} item xs={6} sm={4} md={3}>
                          <Link
                            to={`${folder?.id || ''}`}
                            style={{ textDecoration: 'none' }}
                          >
                            <Card
                              onClick={() => {
                                searchTerm && setSearchTerm('')
                              }}
                              variant="outlined"
                            >
                              <div
                                className={classes.folderContent}
                                data-testid={`downloadablesFolder: ${folder?.name}`}
                              >
                                <FolderIcon color="action" />
                                <Typography className={classes.folderText}>
                                  {folder?.name}
                                </Typography>
                              </div>
                            </Card>
                          </Link>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  py="4px"
                >
                  <Typography variant="h6">
                    {I18n.t('downloadables.files')}
                  </Typography>
                  <Box display="flex">
                    <SelectAll
                      showText
                      digitalAssets={digitalAssets}
                      selected={selected}
                      onChange={setSelected}
                    />
                    <ToggleButtonGroup
                      value={displayType}
                      exclusive
                      size="small"
                      onChange={handleDisplayTypeChange}
                    >
                      <ToggleButton value="grid">
                        <AppsIcon fontSize="small" />
                      </ToggleButton>
                      <ToggleButton value="table">
                        <TocIcon fontSize="small" />
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                <Divider sx={{ marginTop: 1, marginBottom: 2 }} />
                {digitalAssets.length > 0 ? (
                  <>
                    {displayType === 'grid' && (
                      <Grid container spacing={4}>
                        {digitalAssets.map((digitalAsset) => (
                          <Grid key={digitalAsset.id} item xs={6} sm={4} md={3}>
                            <DigitalAssetCard
                              digitalAsset={digitalAsset}
                              isSelected={!!selected[digitalAsset.id]}
                              onSelect={handleSelect}
                              onShowPreview={handleShowPreview}
                              assetsSelected={!!assetsSelected}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                    {displayType === 'table' && (
                      <DigitalAssetTable
                        selectAll={
                          <SelectAll
                            digitalAssets={digitalAssets}
                            selected={selected}
                            onChange={setSelected}
                          />
                        }
                      >
                        {digitalAssets.map((digitalAsset) => (
                          <DigitalAssetRow
                            key={digitalAsset.id}
                            onShowPreview={handleShowPreview}
                            brands={brands}
                            digitalAsset={digitalAsset}
                            isSelected={!!selected[digitalAsset.id]}
                            onSelect={handleSelect}
                          />
                        ))}
                      </DigitalAssetTable>
                    )}
                  </>
                ) : (
                  <EmptyFolder
                    show={digitalAssets.length === 0}
                    searchTerm={debouncedSearchTerm}
                  />
                )}
              </>
            )}
          </div>
        </div>

        {assetPreview && (
          <DigitalAssetDialog
            open={!!assetPreview}
            digitalAsset={assetPreview}
            onClose={handlePreviewClose}
          />
        )}
        <SelectedAssetsFooter selected={selected} setSelected={setSelected} />
      </div>
    </DownloadablesWrapper>
  )
}

type EmptyFolderProps = {
  show: boolean
  searchTerm: string
}

const EmptyFolder: React.FC<React.PropsWithChildren<EmptyFolderProps>> = ({
  show,
  searchTerm,
}) => {
  return (
    <Grow in={show} timeout={600}>
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        height="60%"
        justifyContent="center"
        width="100%"
        color="textSecondary"
      >
        {searchTerm.length ? (
          <ImageSearchIcon color="disabled" style={{ fontSize: 50 }} />
        ) : (
          <FolderOutlinedIcon color="disabled" style={{ fontSize: 50 }} />
        )}
        <Typography color="textSecondary" variant="h6">
          {searchTerm.length
            ? I18n.t('downloadables.emptySearch', { searchTerm })
            : I18n.t('downloadables.emptyFolder')}
        </Typography>
      </Box>
    </Grow>
  )
}

export default Downloadables

const createBreadCrumbs = (createBreadcrumbsArgs: {
  currentPath: string
  allFolders: DigitalAsset[]
}) => {
  const { currentPath, allFolders } = createBreadcrumbsArgs
  if (allFolders.length && currentPath) {
    const childBreadcrumbs = currentPath.split('/').map((folderId) => {
      const folder = allFolders.find(
        (folder: DigitalAsset) => folder.id === folderId,
      )
      if (folder) {
        const { id, name } = folder
        return { to: id, name }
      }
      return { to: '', name: undefined }
    })

    return childBreadcrumbs
  }
  return []
}

const GET_DIGITAL_ASSETS = gql`
  query GetDamAssets($params: GetDigitalAssetsParams!) {
    getDamAssets(params: $params) {
      assets {
        associable_id
        is_folder
        tag_list
        external_video_url
        aspect_ratio_maximum
        aspect_ratio_minimum
        category
        created_at
        crop_height
        crop_origin_x
        crop_origin_y
        crop_width
        description
        digital_asset_id
        digital_asset_associable_id
        download_url
        eligible_share_types {
          facebook {
            link
            photo
            video
          }
        }
        file {
          id
          public_id
          version
          width
          height
          format
          resource_type
          path
          bytes
        }
        file_url
        id
        name
        original_share_image_url
        resource_type
        responsive_share_image_url
        share_image_url
        async_completed_at
        source
        updated_at
      }
      path
    }
  }
`

const GET_FOLDERS = gql`
  query GetFolders($params: GetDigitalAssetsParams!) {
    getDigitalAssets(params: $params) {
      id
      name
    }
  }
`
